import { Fragment, type ReactNode } from 'react';

import { ImageModule } from '#components/Image/ImageModule';
import { Link } from '#components/Link/Link';
import { LiveStreamOverlay } from '#components/Media/LiveStreamOverlay';
import type { SectionSubjectNewsWeather, Teaser } from '#domains/resolver/board-section-data';
import { lg, xl } from '#shared/styles/breakpoints';

import styles from './BoardSectionItemHeroNewsteaser.module.scss';

const TEASER_SIZES = `(max-width: ${lg}px) 50vw, (max-width: ${xl}px) 12vw, 256px`;

type Props = {
	teasers: [Teaser, Teaser, Teaser];
	title: SectionSubjectNewsWeather['title'];
	embedBeforeIndex?: number;
	embedContent?: ReactNode;
	className?: string;
};

export function BoardSectionItemHeroNewsteaser({ teasers, title, embedBeforeIndex, embedContent, className = '' }: Props) {
	let headline: ReactNode;

	if (!title?.link) {
		headline = title?.text ? <h2>{title.text}</h2> : null;
	} else {
		headline = title.text ? (
			<Link href={title.link}>
				<h2>{title.text}</h2>
			</Link>
		) : null;
	}

	return (
		<div className={`${styles.newsTeaserDiv} ${className}`}>
			<div
				data-theme="dark"
				className={`${styles.newsTeaserHeadline} ${title?.showHeroTitleForMobile ? styles.showMobileTitle : styles.showDesktopTitle}`}
			>
				{headline}
			</div>

			{teasers
				.filter((teaser) => teaser.type !== 'empty' && teaser.type !== 'embed')
				.map((teaser, index) => {
					let image: ReactNode;

					if (teaser.type === 'article' && teaser.media.type === 'livestream') {
						const overlayImage = {
							...teaser.media.thumbnail,
							sizes: TEASER_SIZES,
						};

						const livestream = {
							rowId: teaser.media.rowId,
							hls: teaser.media.url,
							start: teaser.media.startDate,
							end: teaser.media.endDate,
							hasRecordings: teaser.media.onDemandVideos.length > 0,
						};

						image = <LiveStreamOverlay image={overlayImage} livestream={livestream} className={styles.newsTeaserItemImage} dense={true} />;
					} else {
						image = (
							<ImageModule className={styles.newsTeaserItemImage} image={teaser.media} enableZoomEffect={true} sizes={TEASER_SIZES} />
						);
					}
					const href = teaser.type === 'link' ? teaser.link.href : teaser.link;
					const title = teaser.type === 'link' ? teaser.link.text : teaser.title;

					return (
						<Fragment key={teaser.rowId}>
							{embedBeforeIndex === index && embedContent}
							<Link className={styles.newsTeaserItem} href={href} title={title}>
								{image}
								<h3 className={`heading4 ${styles.newsTeaserItemText}`}>{teaser.title}</h3>
							</Link>
						</Fragment>
					);
				})}
		</div>
	);
}
